export const STEP_PENDENTE = 1;
export const STEP_EXECUTANDO = 2;
export const STEP_CONCLUIDO = 3;
export const STEP_ERRO = 4;
export const STEP_PULADO = 5;

export const TIPO_SANDBOX = 'sandbox';
export const TIPO_PRODUCAO = 'producao';

export const PIPELINE_RASCUNHO = 1;
export const PIPELINE_EXECUTANDO = 2;
export const PIPELINE_CONCLUIDO = 3;
export const PIPELINE_FALHOU = 4;
/** Encerrado pela metade de propósito, para o próximo deploy poder começar. */
export const PIPELINE_ABANDONADO = 5;

export interface InfraNo {
    chave: string;
    /** Numero de leitura do passo, para citar ele sem repetir o rotulo. */
    numero: number;
    rotulo: string;
    resumo: string;
    destino: 'admin' | 'farm' | 'aws' | 'github' | 'config' | 'validacao' | string;
    nivel: number;
    depende: Array<string>;
    situacao: number;
    tentativas: number;
    liberado: boolean;
    bloqueio: string;
    /** Faixa onde o passo e desenhado, no formato grupo.faixa. */
    faixa: string | null;
    grupo: string | null;
    /** Se o passo entrou nesta execucao. */
    ligado: boolean;
    reversivel: boolean;
    avisoReversao: string | null;
    run: {
        numero: number;
        situacao: number;
        ms: number | null;
        erro: string | null;
        subpassos: number;
    } | null;
}

export interface InfraAresta {
    de: string;
    para: string;
}

export interface InfraPipelineCabecalho {
    id: number;
    tipo: string;
    nome: string;
    slug: string;
    dominio: string;
    farm: string;
    situacao: number;
    contexto: any;
    sandbox: string;
    sandboxAdmin: string;
    criacao: string | null;
    /**
     * Qual deploy de produção está aberto agora, ou null se nenhum. Vem junto
     * do pipeline e não do histórico, então acompanha o polling: se o deploy
     * conclui enquanto a tela está aberta, os botões mudam sozinhos.
     */
    producaoAbertaId: number | null;
}

/**
 * Um pedaco opcional do fluxo. Pipeline sem ramos vem com a lista vazia e roda
 * inteiro.
 */
export interface InfraRamo {
    chave: string;
    rotulo: string;
    conta: string | null;
    regiao: string | null;
    /** Ramo que este arrasta consigo: pedir Farm1 exige a imagem nova. */
    precisa: string | null;
    ligado: boolean;
}

/**
 * Em que seção da barra a etapa aparece.
 *
 * A ordem do deploy não é a ordem do desenho: a Pano vem antes da Farm1 porque
 * ela é o canário, e as máquinas fixas nem passam pela AMI.
 *
 * Front e APIs são seções separadas porque são grupos separados na AWS e
 * trocam sem depender um do outro. Farm1 e Virginia aparecem só em Front: elas
 * têm um grupo só, que atende tudo.
 */
export type SecaoDoRamo = 'imagem' | 'front' | 'apis' | 'jobs' | 'admins';

export const linhaDoRamo = (chave: string): SecaoDoRamo => {
    // 'build' e o web e 'store' e a store: as duas sao imagem de container, e
    // 'imagem' e a AMI. Os tres nomes vivem na mesma seção porque e tudo o que
    // se gera antes de tocar em farm.
    if (chave === 'build' || chave === 'store' || chave === 'imagem') {
        return 'imagem';
    }
    if (chave.indexOf('jobs_') === 0) {
        return 'jobs';
    }
    if (chave.indexOf('admin_') === 0) {
        return 'admins';
    }
    // Sufixo _apis é o que separa: pano_apis e farm2_apis vão para APIs, o
    // resto da frota fica em Front.
    //
    // slice e não lastIndexOf: com lastIndexOf, a chave 'pano' caía em APIs.
    // Ela tem 4 letras, então length - 5 dá -1, que é exatamente o que
    // lastIndexOf devolve quando não acha nada, e a comparação dava certo por
    // acidente.
    if (chave.slice(-5) === '_apis') {
        return 'apis';
    }
    return 'front';
};

/** Uma linha do desenho, dentro de um quadro. */
export interface InfraFaixa {
    chave: string;
    rotulo: string;
    grupo: string | null;
    grupoRotulo: string | null;
    ligado: boolean;
}

export interface InfraGrafo {
    pipeline: InfraPipelineCabecalho;
    nos: Array<InfraNo>;
    arestas: Array<InfraAresta>;
    ramos: Array<InfraRamo>;
    faixas: Array<InfraFaixa>;
}

export interface InfraPipelineLinha {
    id: number;
    tipo: string;
    nome: string;
    slug: string;
    dominio: string;
    farm: string;
    situacao: number;
    total: number;
    concluidos: number;
    erros: number;
    sandbox: string | null;
    atualizado: string | null;
}

/** O que o card de Ambiente Producao mostra no topo da Central. */
/**
 * Como um deploy de produção se chama na tela.
 *
 * O número, e não a data: é por ele que se cita um deploy numa conversa, e a
 * data já aparece do lado em todo lugar que mostra o nome. Montado aqui e não
 * no backend porque assim os deploys antigos, que têm data e hora gravadas no
 * nome, também passam a aparecer pelo número.
 */
export const rotuloDeploy = (id: number): string => 'Deploy de produção #' + id;

/** Uma execução anterior do deploy de produção, para o histórico. */
export interface InfraProducaoLinha {
    id: number;
    situacao: number;
    total: number;
    concluidos: number;
    erros: number;
    /** Quem abriu o deploy. Null nos que nasceram sem usuário na sessão. */
    usuario: string | null;
    criacao: string | null;
}

export interface InfraProducaoHistorico {
    /** Qual deploy está aberto agora, ou null se nenhum. */
    abertoId: number | null;
    rows: Array<InfraProducaoLinha>;
    /** Quantos deploys existem no total, não nesta página. */
    total: number;
    pagina: number;
    paginas: number;
    porPagina: number;
}

export interface InfraProducaoResumo {
    aberta: boolean;
    total: number;
    /** Passos que já tiveram tentativa. Diz se há trabalho a perder ao abandonar. */
    comTentativa: number;
    pipeline: {
        id: number;
        nome: string;
        situacao: number;
        total: number;
        concluidos: number;
        erros: number;
        atualizado: string | null;
    } | null;
}

export const descricaoSituacaoStep = (situacao: number): string => {
    switch (situacao) {
        case STEP_EXECUTANDO: return 'Executando';
        case STEP_CONCLUIDO: return 'Concluído';
        case STEP_ERRO: return 'Erro';
        case STEP_PULADO: return 'Pulado';
        default: return 'Pendente';
    }
};

export const descricaoSituacaoPipeline = (situacao: number): string => {
    switch (situacao) {
        case PIPELINE_EXECUTANDO: return 'Em andamento';
        case PIPELINE_CONCLUIDO: return 'Concluído';
        case PIPELINE_FALHOU: return 'Com falha';
        case PIPELINE_ABANDONADO: return 'Abandonado';
        default: return 'Rascunho';
    }
};

export const corSituacaoPipeline = (situacao: number): string => {
    switch (situacao) {
        case PIPELINE_EXECUTANDO: return 'info';
        case PIPELINE_CONCLUIDO: return 'success';
        case PIPELINE_FALHOU: return 'danger';
        case PIPELINE_ABANDONADO: return 'dark';
        default: return 'secondary';
    }
};

export const rotuloDestino = (destino: string): string => {
    switch (destino) {
        case 'admin': return 'Admin';
        case 'farm': return 'Farm';
        case 'aws': return 'AWS';
        case 'github': return 'GitHub';
        case 'config': return 'Config';
        case 'validacao': return 'Validação';
        case 'template': return 'Template';
        default: return destino;
    }
};
